'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { trackFormSubmission } from '@/lib/analytics';

interface FormData {
  imie: string;
  email: string;
  telefon: string;
  platformy: string;
  zamowienia: string;
  oferty: string;
  magazyny: string;
  wms: string;
  fakturowanie: string;
  oss: string;
  wiadomosc: string;
}

const initialForm: FormData = {
  imie: '',
  email: '',
  telefon: '',
  platformy: '',
  zamowienia: '',
  oferty: '',
  magazyny: '',
  wms: '',
  fakturowanie: '',
  oss: '',
  wiadomosc: '',
};

interface ContactInfo {
  email: string;
  phone: string;
  address: string;
}

export default function KontaktSection() {
  const [form, setForm] = useState<FormData>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    email: 'kontakt.emtgroup@gmail.com',
    phone: '882-639-186',
    address: 'Barczewo, Polska',
  });
  const supabase = createClient();

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const { data, error } = await supabase.from('settings').select('key, value');

        if (error) {
          console.error('Error fetching settings:', error.message);
          return;
        }

        const settingsMap: Record<string, string> = {};
        data?.forEach((setting) => {
          settingsMap[setting.key] = setting.value;
        });

        setContactInfo({
          email: settingsMap['contact_email'] || 'kontakt.emtgroup@gmail.com',
          phone: settingsMap['contact_phone'] || '882-639-186',
          address: settingsMap['contact_address'] || 'Barczewo, Polska',
        });
      } catch (err) {
        console.error('Error:', err);
      }
    };

    fetchContactInfo();
  }, [supabase]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Call API route instead of direct Supabase insert
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('Error submitting form:', result.error);
        // Display actual error from API with details if available
        const errorMessage = result.details 
          ? `${result.error}: ${result.details}` 
          : result.error || 'Wystąpił błąd podczas wysyłania formularza. Spróbuj ponownie.';
        setError(errorMessage);
        return;
      }

      // Show warnings in console if emails failed but lead was saved
      if (result.warnings && result.warnings.length > 0) {
        console.warn('Form submitted with warnings:', result.warnings);
      }

      // Track successful form submission
      trackFormSubmission('contact_form', {
        platformy: form.platformy,
        zamowienia: form.zamowienia,
        fakturowanie: form.fakturowanie,
      });

      setSubmitted(true);
      setForm(initialForm);
    } catch (err: any) {
      console.error('Error:', err);
      // Show actual error message from exception
      setError(`Błąd: ${err.message || 'Wystąpił błąd podczas wysyłania formularza. Spróbuj ponownie.'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="py-14 md:py-20 bg-navy">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left: copy */}
          <div className="lg:col-span-4 space-y-8">
            <div className="space-y-3">
              <p className="text-xs font-700 uppercase tracking-wide text-blue-DEFAULT" style={{ fontFamily: 'Inter, sans-serif' }}>Kontakt</p>
              <h2 className="text-4xl md:text-5xl font-700 tracking-tight text-white leading-[1.0]" style={{ fontFamily: 'Inter, sans-serif' }}>
                Zacznijmy<br />
                <span className="text-blue-DEFAULT">od rozmowy.</span>
              </h2>
            </div>
            <p className="text-base text-white/50 font-400 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
              Wypełnij formularz, a odezwiemy się w ciągu jednego dnia roboczego
              z bezpłatną oceną Twojej sytuacji i propozycją zakresu wdrożenia.
            </p>

            {/* Contact details */}
            <div className="space-y-4 pt-4 border-t border-white/10">
              <div className="flex items-center gap-4">
                <div className="p-2 rounded-lg bg-white/5">
                  <Icon name="EnvelopeIcon" size={16} className="text-blue-DEFAULT" variant="outline" />
                </div>
                <div>
                  <div className="text-xs font-700 uppercase tracking-wide text-white/30" style={{ fontFamily: 'Inter, sans-serif' }}>
                    E-mail
                  </div>
                  <a href={`mailto:${contactInfo.email}`} className="text-sm font-500 text-white/80 hover:text-blue-light transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {contactInfo.email}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-2 rounded-lg bg-white/5">
                  <Icon name="PhoneIcon" size={16} className="text-blue-DEFAULT" variant="outline" />
                </div>
                <div>
                  <div className="text-xs font-700 uppercase tracking-wide text-white/30" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Telefon
                  </div>
                  <a href={`tel:+48${contactInfo.phone.replace(/[^0-9]/g, '')}`} className="text-sm font-500 text-white/80 hover:text-blue-light transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {contactInfo.phone}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-2 rounded-lg bg-white/5">
                  <Icon name="MapPinIcon" size={16} className="text-blue-DEFAULT" variant="outline" />
                </div>
                <div>
                  <div className="text-xs font-700 uppercase tracking-wide text-white/30" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Siedziba
                  </div>
                  <div className="text-sm font-500 text-white/80" style={{ fontFamily: 'Inter, sans-serif' }}>{contactInfo.address}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-8">
            {submitted ? (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center space-y-4">
                <Icon name="CheckCircleIcon" size={48} className="text-blue-DEFAULT mx-auto" variant="solid" />
                <h3 className="text-2xl font-700 text-white" style={{ fontFamily: 'Inter, sans-serif' }}>Wiadomość wysłana!</h3>
                <p className="text-base text-white/50 font-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Odezwiemy się w ciągu jednego dnia roboczego.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 text-sm">
                    {error}
                  </div>
                )}
                {/* Row 1: imie, email, telefon */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="form-field">
                    <label htmlFor="imie" className="form-label">Imię i nazwisko *</label>
                    <input
                      id="imie"
                      name="imie"
                      type="text"
                      required
                      className="form-input"
                      placeholder="Jan Kowalski"
                      value={form.imie}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="email" className="form-label">E-mail *</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="form-input"
                      placeholder="jan@sklep.pl"
                      value={form.email}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="telefon" className="form-label">Telefon</label>
                    <input
                      id="telefon"
                      name="telefon"
                      type="tel"
                      className="form-input"
                      placeholder="+48 500 000 000"
                      value={form.telefon}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Row 2: platformy */}
                <div className="form-field">
                  <label htmlFor="platformy" className="form-label">Platformy sprzedaży *</label>
                  <input
                    id="platformy"
                    name="platformy"
                    type="text"
                    required
                    className="form-input"
                    placeholder="np. Allegro, Amazon DE/FR, sklep PrestaShop"
                    value={form.platformy}
                    onChange={handleChange}
                  />
                </div>

                {/* Row 3: zamowienia, oferty, magazyny */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="form-field">
                    <label htmlFor="zamowienia" className="form-label">Zamówień dziennie</label>
                    <select
                      id="zamowienia"
                      name="zamowienia"
                      className="form-input form-select"
                      value={form.zamowienia}
                      onChange={handleChange}
                    >
                      <option value="">Wybierz</option>
                      <option>do 50</option>
                      <option>50–200</option>
                      <option>200–500</option>
                      <option>500–2000</option>
                      <option>powyżej 2000</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label htmlFor="oferty" className="form-label">Liczba ofert/SKU</label>
                    <select
                      id="oferty"
                      name="oferty"
                      className="form-input form-select"
                      value={form.oferty}
                      onChange={handleChange}
                    >
                      <option value="">Wybierz</option>
                      <option>do 500</option>
                      <option>500–5 000</option>
                      <option>5 000–50 000</option>
                      <option>powyżej 50 000</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label htmlFor="magazyny" className="form-label">Liczba magazynów</label>
                    <select
                      id="magazyny"
                      name="magazyny"
                      className="form-input form-select"
                      value={form.magazyny}
                      onChange={handleChange}
                    >
                      <option value="">Wybierz</option>
                      <option>1</option>
                      <option>2–3</option>
                      <option>4+</option>
                      <option>dropshipping</option>
                    </select>
                  </div>
                </div>

                {/* Row 4: WMS, fakturowanie, OSS */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="form-field">
                    <label htmlFor="wms" className="form-label">System WMS</label>
                    <input
                      id="wms"
                      name="wms"
                      type="text"
                      className="form-input"
                      placeholder="np. Subiekt, własny WMS, brak"
                      value={form.wms}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="fakturowanie" className="form-label">System faktur</label>
                    <select
                      id="fakturowanie"
                      name="fakturowanie"
                      className="form-input form-select"
                      value={form.fakturowanie}
                      onChange={handleChange}
                    >
                      <option value="">Wybierz</option>
                      <option>wFirma</option>
                      <option>Fakturownia</option>
                      <option>InFakt</option>
                      <option>iFirma</option>
                      <option>Inny</option>
                      <option>Brak</option>
                      <option>Nie posiadam / Excel (chcę wdrożyć KSeF)</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label htmlFor="oss" className="form-label">Faktury OSS?</label>
                    <select
                      id="oss"
                      name="oss"
                      className="form-input form-select"
                      value={form.oss}
                      onChange={handleChange}
                    >
                      <option value="">Wybierz</option>
                      <option>Tak, potrzebuję OSS</option>
                      <option>Nie</option>
                      <option>Nie wiem, co to OSS</option>
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div className="form-field">
                  <label htmlFor="wiadomosc" className="form-label">Wiadomość</label>
                  <textarea
                    id="wiadomosc"
                    name="wiadomosc"
                    rows={4}
                    className="form-input resize-none"
                    placeholder="Opisz krótko swój projekt lub problem do rozwiązania..."
                    value={form.wiadomosc}
                    onChange={handleChange}
                  />
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-DEFAULT text-white font-800 text-sm py-4 px-8 rounded-xl transition-all duration-300 ease-out hover:bg-blue-light hover:scale-105 hover:shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:active:scale-100"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {loading ? 'Wysyłanie...' : 'Wyślij zapytanie'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}